import React, { useState } from 'react';
import { aiKnowledgeApi } from '../../services/apiAiKnowledge';
import { makeStyles } from '@material-ui/core/styles';

const normalizeStep = (step) => {
  if (typeof step === 'string') {
    return {
      text: step,
      imageUrl: '',
      imageName: '',
    };
  }

  if (step && typeof step === 'object') {
    return {
      text: step.text || step.description || '',
      imageUrl: step.imageUrl || '',
      imageName: step.imageName || '',
    };
  }

  return {
    text: '',
    imageUrl: '',
    imageName: '',
  };
};

const getStepImageSrc = (imageUrl) => {
  if (!imageUrl) return '';
  if (/^https?:\/\//i.test(imageUrl)) return imageUrl;

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  return `${backendUrl}${imageUrl.startsWith('/') ? imageUrl : `/${imageUrl}`}`;
};

const normalizeInitialData = (data) => {
  let steps = [];

  if (Array.isArray(data?.steps)) {
    steps = data.steps.map(normalizeStep);
  } else if (typeof data?.steps === 'string' && data.steps.trim() !== '') {
    try {
      const parsed = JSON.parse(data.steps);
      steps = Array.isArray(parsed) ? parsed.map(normalizeStep) : [];
    } catch {
      steps = [];
    }
  } else if (data?.steps === null || data?.steps === undefined) {
    steps = [];
  }

  return {
    keyword: data?.keyword ?? '',
    tags: Array.isArray(data?.tags)
      ? data.tags.join(', ')
      : typeof data?.tags === 'string'
        ? data.tags
        : '',
    system: data?.system ?? '',
    question: data?.question ?? '',
    answer: data?.answer ?? '',
    steps: steps,
    priority: data?.priority ?? 1,
    isActive: Boolean(data?.isActive ?? true),
  };
};
const useStyles = makeStyles((theme) => ({
  container: {
    width: '100%',
    maxWidth: '700px',
    margin: '0 auto',
  },
  form: {
    backgroundColor: 'white',
    padding: '30px',
    borderRadius: '12px',
    boxShadow:
      '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    border: '1px solid #e5e7eb',
  },
  formGroup: {
    marginBottom: '24px',
  },
  label: {
    display: 'block',
    marginBottom: '8px',
    fontWeight: '600',
    color: '#374151',
    fontSize: '14px',
  },
  input: {
    width: '100%',
    padding: '12px 16px',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    fontSize: '14px',
    transition: 'all 0.2s ease',
    backgroundColor: 'white',
    boxSizing: 'border-box',
    '&:focus': {
      outline: 'none',
      borderColor: '#3b82f6',
      boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
    },
  },
  textarea: {
    width: '100%',
    padding: '12px 16px',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    fontSize: '14px',
    transition: 'all 0.2s ease',
    backgroundColor: 'white',
    resize: 'vertical',
    minHeight: '80px',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    '&:focus': {
      outline: 'none',
      borderColor: '#3b82f6',
      boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
    },
  },
  button: {
    padding: '12px 24px',
    backgroundColor: '#3b82f6',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:hover:not(:disabled)': {
      backgroundColor: '#2563eb',
      transform: 'translateY(-1px)',
    },
    '&:disabled': {
      backgroundColor: '#9ca3af',
      cursor: 'not-allowed',
      transform: 'none',
    },
  },
  stepContainer: {
    display: 'flex',
    gap: '8px',
    marginBottom: '12px',
  },
  stepInput: {
    flex: 1,
    padding: '12px 16px',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    fontSize: '14px',
    transition: 'all 0.2s ease',
    '&:focus': {
      outline: 'none',
      borderColor: '#3b82f6',
      boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
    },
  },
  stepList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
  },
  stepItem: {
    display: 'grid',
    gridTemplateColumns: '1fr auto',
    gap: '12px',
    alignItems: 'start',
    padding: '12px 16px',
    backgroundColor: '#f8fafc',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    marginBottom: '8px',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: '#f1f5f9',
    },
  },
  stepContent: {
    minWidth: 0,
  },
  stepText: {
    color: '#374151',
    fontSize: '14px',
    lineHeight: 1.5,
    overflowWrap: 'anywhere',
  },
  stepActions: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
  },
  uploadButton: {
    padding: '6px 12px',
    backgroundColor: '#2563eb',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: '#1d4ed8',
      transform: 'translateY(-1px)',
    },
  },
  hiddenFileInput: {
    display: 'none',
  },
  stepImagePreview: {
    marginTop: '10px',
    maxWidth: '260px',
    borderRadius: '8px',
    border: '1px solid #e5e7eb',
    display: 'block',
  },
  removeImageButton: {
    marginTop: '8px',
    padding: '5px 10px',
    backgroundColor: '#f3f4f6',
    color: '#374151',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    fontSize: '12px',
    cursor: 'pointer',
  },
  removeButton: {
    padding: '6px 12px',
    backgroundColor: '#ef4444',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: '#dc2626',
      transform: 'translateY(-1px)',
    },
  },
  select: {
    width: '100%',
    padding: '12px 16px',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    fontSize: '14px',
    backgroundColor: 'white',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    '&:focus': {
      outline: 'none',
      borderColor: '#3b82f6',
      boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
    },
  },
  checkboxContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 0',
  },
  checkbox: {
    width: '18px',
    height: '18px',
    cursor: 'pointer',
    accentColor: '#3b82f6',
  },
  sectionTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#374151',
    marginBottom: '16px',
    paddingBottom: '8px',
    borderBottom: '1px solid #e5e7eb',
  },
  fullWidthButton: {
    width: '100%',
    marginTop: '20px',
  },
  helperText: {
    marginTop: '6px',
    color: '#6b7280',
    fontSize: '12px',
    lineHeight: 1.5,
  },
}));

const normalizeTags = (tags) => {
  if (!tags) return [];

  if (Array.isArray(tags)) {
    return tags
      .flatMap(normalizeTags)
      .map((tag) =>
        String(tag)
          .replace(/[\[\]"]/g, '')
          .trim()
      )
      .filter(Boolean);
  }

  return String(tags)
    .replace(/^\s*\[/, '')
    .replace(/\]\s*$/, '')
    .split(',')
    .map((tag) => tag.replace(/[\[\]"]/g, '').trim())
    .filter(Boolean);
};

const AiKnowledgeForm = ({ onSave, initialData }) => {
  const [formData, setFormData] = useState(
    normalizeInitialData(initialData ?? {})
  );
  const [loading, setLoading] = useState(false);
  const [suggestingTags, setSuggestingTags] = useState(false);
  const [uploadingStepIndex, setUploadingStepIndex] = useState(null);
  const [currentStep, setCurrentStep] = useState('');
  const classes = useStyles();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const stepsToSave = Array.isArray(formData.steps) ? formData.steps : [];

    const payload = {
      ...formData,
      tags: normalizeTags(formData.tags),
      steps: stepsToSave,
    };

    try {
      if (initialData?.id) {
        await aiKnowledgeApi.update(initialData.id, payload);
        alert('Conhecimento atualizado com sucesso!');
      } else {
        await aiKnowledgeApi.create(payload);
        alert('Conhecimento criado com sucesso!');
      }

      onSave?.();
      setFormData(normalizeInitialData({}));
    } catch (error) {
      console.error('Erro ao salvar:', error);
      alert('Erro ao salvar conhecimento');
    } finally {
      setLoading(false);
    }
  };

  const addStep = () => {
    if (!currentStep.trim()) return;

    setFormData((prev) => ({
      ...prev,
      steps: [
        ...(prev.steps || []),
        { text: currentStep.trim(), imageUrl: '', imageName: '' },
      ],
    }));

    setCurrentStep('');
  };

  const removeStep = (index) => {
    setFormData((prev) => ({
      ...prev,
      steps: (prev.steps || []).filter((_, i) => i !== index),
    }));
  };

  const removeStepImage = (index) => {
    setFormData((prev) => ({
      ...prev,
      steps: (prev.steps || []).map((step, i) =>
        i === index
          ? {
              ...normalizeStep(step),
              imageUrl: '',
              imageName: '',
            }
          : normalizeStep(step)
      ),
    }));
  };

  const handleStepImageUpload = async (index, file) => {
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Selecione apenas arquivos de imagem.');
      return;
    }

    const uploadData = new FormData();
    uploadData.append('image', file);
    setUploadingStepIndex(index);

    try {
      const response = await aiKnowledgeApi.uploadStepImage(uploadData);
      const imageUrl = response?.data?.imageUrl || '';
      const imageName = response?.data?.originalName || file.name;

      setFormData((prev) => ({
        ...prev,
        steps: (prev.steps || []).map((step, i) =>
          i === index
            ? {
                ...normalizeStep(step),
                imageUrl,
                imageName,
              }
            : normalizeStep(step)
        ),
      }));
    } catch (error) {
      console.error('Erro ao enviar print:', error);
      alert('Erro ao enviar print da etapa');
    } finally {
      setUploadingStepIndex(null);
    }
  };

  const handleSuggestTags = async () => {
    setSuggestingTags(true);
    try {
      const response = await aiKnowledgeApi.suggestTags({
        keyword: formData.keyword,
        system: formData.system,
        question: formData.question,
        answer: formData.answer,
      });

      const suggestedTags = Array.isArray(response?.data?.tags)
        ? response.data.tags.join(', ')
        : '';

      setFormData((prev) => ({
        ...prev,
        tags: suggestedTags,
      }));
    } catch (error) {
      console.error('Erro ao sugerir tags:', error);
      alert('Erro ao sugerir tags');
    } finally {
      setSuggestingTags(false);
    }
  };

  const stepsArray = Array.isArray(formData.steps) ? formData.steps : [];

  return (
    <div className={classes.container}>
      <form onSubmit={handleSubmit} className={classes.form}>
        {/* Campos principais */}
        <div className={classes.formGroup}>
          <label className={classes.label}>Palavra-chave *</label>
          <input
            type="text"
            value={formData.keyword}
            onChange={(e) =>
              setFormData({ ...formData, keyword: e.target.value })
            }
            required
            className={classes.input}
          />
          <div className={classes.helperText}>
            Use este campo como um resumo livre da intenção principal.
          </div>
        </div>

        <div className={classes.formGroup}>
          <label className={classes.label}>Tags de busca</label>
          <input
            type="text"
            value={formData.tags}
            onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
            className={classes.input}
            placeholder="ex: falta documentos, fornecedor, inabilitação, prazo edital"
          />
          <div className={classes.helperText}>
            Separe por vírgula. Essas tags ajudam a IA a encontrar a resposta
            certa mesmo com perguntas informais.
          </div>
          <button
            type="button"
            onClick={handleSuggestTags}
            className={classes.button}
            style={{ marginTop: '12px' }}
            disabled={
              suggestingTags ||
              (!formData.question && !formData.answer && !formData.keyword)
            }
          >
            {suggestingTags ? 'Sugerindo tags...' : 'Sugerir tags com IA'}
          </button>
        </div>

        <div className={classes.formGroup}>
          <label className={classes.label}>Sistema *</label>
          <input
            type="text"
            value={formData.system}
            onChange={(e) =>
              setFormData({ ...formData, system: e.target.value })
            }
            required
            className={classes.input}
          />
        </div>

        <div className={classes.formGroup}>
          <label className={classes.label}>Pergunta *</label>
          <textarea
            value={formData.question}
            onChange={(e) =>
              setFormData({ ...formData, question: e.target.value })
            }
            required
            rows="3"
            className={classes.textarea}
          />
        </div>

        <div className={classes.formGroup}>
          <label className={classes.label}>Resposta *</label>
          <textarea
            value={formData.answer}
            onChange={(e) =>
              setFormData({ ...formData, answer: e.target.value })
            }
            required
            rows="5"
            className={classes.textarea}
          />
        </div>

        {/* Passos para solução */}
        <div className={classes.formGroup}>
          <h3 className={classes.sectionTitle}>Passos para solução</h3>

          <div className={classes.stepContainer}>
            <input
              type="text"
              value={currentStep}
              onChange={(e) => setCurrentStep(e.target.value)}
              placeholder="Digite um passo..."
              className={classes.stepInput}
              onKeyPress={(e) =>
                e.key === 'Enter' && (e.preventDefault(), addStep())
              }
            />
            <button type="button" onClick={addStep} className={classes.button}>
              Adicionar
            </button>
          </div>

          {stepsArray.length > 0 && (
            <ul className={classes.stepList}>
              {stepsArray.map((step, index) => (
                <li key={index} className={classes.stepItem}>
                  <div className={classes.stepContent}>
                    <div className={classes.stepText}>
                      {normalizeStep(step).text}
                    </div>
                    {normalizeStep(step).imageUrl && (
                      <>
                        <img
                          src={getStepImageSrc(normalizeStep(step).imageUrl)}
                          alt={
                            normalizeStep(step).imageName || 'Print da etapa'
                          }
                          className={classes.stepImagePreview}
                        />
                        <button
                          type="button"
                          onClick={() => removeStepImage(index)}
                          className={classes.removeImageButton}
                        >
                          Remover print
                        </button>
                      </>
                    )}
                  </div>
                  <div className={classes.stepActions}>
                    <input
                      id={`step-image-${index}`}
                      type="file"
                      accept="image/*"
                      className={classes.hiddenFileInput}
                      onChange={(e) => {
                        handleStepImageUpload(index, e.target.files?.[0]);
                        e.target.value = '';
                      }}
                    />
                    <label
                      htmlFor={`step-image-${index}`}
                      className={classes.uploadButton}
                    >
                      {uploadingStepIndex === index ? 'Enviando...' : 'Print'}
                    </label>
                    <button
                      type="button"
                      onClick={() => removeStep(index)}
                      className={classes.removeButton}
                    >
                      Excluir
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Configurações */}
        <div className={classes.formGroup}>
          <h3 className={classes.sectionTitle}>Configurações</h3>

          <div style={{ marginBottom: '16px' }}>
            <label className={classes.label}>Prioridade</label>
            <select
              value={formData.priority}
              onChange={(e) =>
                setFormData({ ...formData, priority: Number(e.target.value) })
              }
              className={classes.select}
            >
              <option value="1">1 - Baixa</option>
              <option value="2">2</option>
              <option value="3">3 - Média</option>
              <option value="4">4</option>
              <option value="5">5 - Alta</option>
            </select>
          </div>

          <div className={classes.checkboxContainer}>
            <input
              type="checkbox"
              checked={formData.isActive}
              onChange={(e) =>
                setFormData({ ...formData, isActive: e.target.checked })
              }
              className={classes.checkbox}
            />
            <label
              className={classes.label}
              style={{ margin: 0, cursor: 'pointer' }}
            >
              Ativo
            </label>
          </div>
        </div>

        {/* Botão de submit */}
        <button
          type="submit"
          disabled={loading}
          className={`${classes.button} ${classes.fullWidthButton}`}
        >
          {loading
            ? 'Salvando...'
            : initialData
              ? 'Atualizar Conhecimento'
              : 'Criar Conhecimento'}
        </button>
      </form>
    </div>
  );
};

export default AiKnowledgeForm;
